import {all, call, delay, fork, put, race, select, take, takeLatest} from 'redux-saga/effects'
import {push} from 'react-router-redux'
import {difference, get, has, isEmpty, isNil} from 'lodash'
import {quotePivotTableFields, quoteSelectorPivotTableFields} from 'containers/quotePivotTable'
import {partFields, partLoadFields} from 'sagas/priceListsSaga'
import types from '../actions/types'
import * as contactActions from '../actions/contacts'
import * as dataActions from '../actions/data'
import * as quotingActions from '../actions/quoting'
import * as dialogActions from '../actions/dialog'
import * as pivotTableActions from '../actions/pivotTable'
import {generalError} from '../actions/index'
import uuid from '../helpers/uuid'
import {reorderItems} from 'helpers/arrays'
import {ONE_SECOND_MS} from 'helpers/dates'
import {round} from 'helpers/financial'
import {isPartTypePricingCalculator, PartType} from 'helpers/parts'
import {snackbarClear, snackbarMessage} from 'actions/snackbar'
import * as onboardingActions from '../actions/onboarding'
import {SKILLS} from 'helpers/onboarding'
import {getInvoiceFinancialYearStart, getPricingAccuracy} from 'selectors/settings'
import {quotingContextApprovalsPivotTableKey} from "reducers/quotingContext/approvals"
import {subItemPivotTableKey} from 'reducers/quotingContext/selected/itemDrawer'
import {quotingContextQuotePivotTableKey} from 'reducers/quotingContext/quotes'
import {activitiesTabPivotTableKey} from 'reducers/quotingContext/selected/activitiesTab'
import {amendmentsPivotTableKey} from 'reducers/quotingContext/selected/amendments'
import {quotingContextQuoteApprovalsPivotTableKey} from "reducers/quotingContext/selected/approvals"
import {quotingContextDocumentsPivotTableKey} from 'reducers/quotingContext/selected/documents'
import {quotingContextNewDocumentsPivotTableKey} from "reducers/quotingContext/selected/newDocuments"
import {invoicesTabPivotTableKey} from 'reducers/quotingContext/selected/invoicesTab'
import {purchasingTabPivotTableKey} from "reducers/quotingContext/selected/purchasingTab"
import {getDateFilters, getDateRangeForKey} from 'helpers/dateRange'
import {getHomeCompanyId, getProSync, getSettings, getUser, getUserSettings} from 'selectors/auth'
import {getAllRows, getSearch} from 'selectors/pivotTable'
import {getPackTypes} from 'selectors/priceLists'
import {getDeliveryNoteItemsForDelivery} from 'helpers/deliveries/deliveryNoteItems'
import {editDataMulti, setEntities, updateData} from 'actions/pivotTable'
import {getCurrencies, getOrderTypes, getTaxCodes} from 'selectors/data'
import {parseUserFields} from 'helpers/fields'
import {getOpenCompanies, getSelectedCompanyId, getSelectedContactId} from 'selectors/contacts'
import {getPathForQuote, getPathForQuotePreview} from 'helpers/navigation'
import {history} from '../app'
import {createGoogleCompanyDocumentWatcher} from 'sagas/pivotTable'
import {setSelectedQuotingTab} from 'actions/navigation'
import {ContactEmailType, getFormDataFor} from 'helpers/email/enum'
import {sortQuickAddOrPriceListTemplateItems} from "helpers/quoting/subItems"
import {deliveriesQuoteSelectorPivotTableKey} from "reducers/deliveriesContext/quoteSelector"
import {getDeliveriesQuoteSelector} from "selectors/deliveries"
import {uploadFileFailure, uploadFileSuccess} from "actions/integration"
import {convertMBToBytes} from "helpers/number"
import {FILE_UPLOAD_CHUNK_SIZE_MB} from "helpers/settings"
import {updateCustomerApprovalStatusFailure, updateCustomerApprovalStatusSuccess} from "actions/customer"
import {
  approvalDrawerFields,
  getApprovalFilterParams,
  getLatestApprovalRevision,
  getUpdatedApprovalRevisions,
} from "helpers/approvals"
import {
  quotingContextSelectorOrderItemsPivotTableKey,
  quotingContextSelectorOrdersPivotTableKey,
} from 'reducers/quotingContext/selector'
import {createDocumentPivotTableWatcher, createNonServerPivotTableWatcher, createPivotTableWatcher} from './pivotTable'
import {
  itemPivotTableKey,
  jobCardsPivotTableKey,
  orderHistoryPivotTableKey,
  quotingContextBillOfLabourPivotTableKey,
  quotingContextBillOfMaterialsPivotTableKey,
  quotingContextDeliveryNotesPivotTableKey,
  quotingContextDeliveryPivotTableKey,
  quotingContextPurchasingItemsPivotTableKey,
} from 'reducers/quotingContext/selected'
import {
  getApprovalsFolderPath,
  getFileAsBytes,
  getFileChunks,
  getFileFormConfig,
  getFileFormData,
  getOrderDocumentsFolderPath,
} from 'helpers/documents'
import {
  canEditQuote,
  filterDeletedItems,
  filterDeletedItemsDeep,
  getCalculatedOrderItem,
  getCalculatedTotals,
  getCompanyId,
  getOrderItemLetter,
  getParamsForType,
  getQuotingFilterParams,
  QuoteStatus,
  updateTaxCodeOnOrder,
  updateTaxCodeOnOrderItems,
} from 'helpers/quotes'
import {
  assemblyHasNestedCalcWizard,
  copyOrderItem as copyOrderItemHelper,
  createOrderItem,
  explodeSubItems,
  findItem,
  findTopParent,
  getActiveSubItems,
  getAllItems,
  getAllSubItems,
  getPricingParams,
  getUpdatedOrderItemForSubItemUpdate,
  getUpdatedParentForSubItemUpdate,
  PriceMode,
  replaceSubItemTree,
  showSubItems,
  updateItems,
} from 'helpers/orders'
import {
  getApprovalsFilters,
  getLastOrder,
  getLastOrderItems,
  getQuotingSelector,
  getSearchField,
  getSelectedCurrentTab as getSelectedQuotingTab,
  getSelectedOrder,
  getSelectedOrderId,
  getSelectedOrderItem,
  getSelectedOrderItems,
  getSelectedQuoteApprovalDrawer,
  getSelectedQuoteApprovalRevisionsDrawer,
  getSelectedQuoteDocumentsCurrentFolder,
  getSelectedQuoteDocumentSubFolder,
  getShowAllPurchaseOrderRevisions,
  getSubItems as getSubItemsSelector,
} from 'selectors/quotes'
import {
  addApprovalFailure,
  addApprovalRevisionFailure,
  addApprovalRevisionSuccess,
  addApprovalSuccess,
  bulkCloseOrRejectOrdersFailure,
  bulkCloseOrRejectOrdersSuccess,
  clearApprovalDocuments,
  getOrderCountForBulkCloseFailure,
  getOrderCountForBulkCloseSuccess,
  hideApprovalDrawer,
  loadApprovalCustomerLinkFailure,
  loadApprovalCustomerLinkSuccess,
  loadApprovalDocuments,
  loadApprovalDocumentsFailure,
  loadApprovalDocumentsSuccess,
  loadApprovalFailure,
  loadApprovalRevisionDocumentsFailure,
  loadApprovalRevisionDocumentsSuccess,
  loadApprovalSuccess,
  loadApprovalUploadUrlFailure,
  loadOrderForDeliveryFailure,
  loadOrderForDeliverySuccess,
  showApprovalDrawer,
  updateApprovalFailure,
  updateApprovalSuccess,
  updateOrder,
} from 'actions/quoting'
import {fetchCalculatorForm} from 'actions/pricingCalculator'
import {
  addSubItemsForPricingCalculators,
  buildFormDefaultInputs,
  createCalculatorDataFromPart,
  getPricingCalculatorKey,
  hasAllRequiredInputs,
  isPricingCalculatorEntity,
  recordDeletedSubItems,
  updateSubItemsWithPricing,
} from "helpers/pricingCalculator"

export const dashboardPanelsMaxQuotes = 30

const quoteItemFields = {
  default: true,
  part: partFields,
  userfields: true,
  calculatorData: {
    default: true,
    inputs: {
      default: true,
      parts: {
        default: true,
        packs: true,
      },
    },
  },
  subitems: {   // enables sub-items in the main items grid
    default: true,
    part: partFields,
    calculatorData: {
      default: true,
      inputs: {
        default: true,
        parts: {
          default: true,
          packs: true,
        },
      },
    },
    subitems: {
      default: true,
      part: partFields,
    },
  },
  jobCard: {
    id: true,
  },
  purchaseOrderItems: true,
}

const loadQuoteFields = {
  default: true,
  prevRevision: true,
  nextRevision: true,
  company: {
    default: true,
    companyLogoImage: true,
    accountingcompany: true,
    contacts: {
      forename: true,
      surname: true,
      companyType: true,
      type: true,
      email: true,
    },
  },
  overrides: true,
  contact: {
    default: true,
    location: true,
  },
  opportunity: true,
  userfields: true,
  summaryinfo: {
    invoicedValue: true,  // for add invoice dialog initial state
  },
  purchaseOrderSummaryInfo: true,
  referralcompany: {
    id: true,
    name: true,
  },
  jobCards: {
    id: true,
    orderId: true,
    orderItemId: true,
    owner: true,
  },
  orderitems: true,
  creator: true,
  estimator: true,
  salesman: true,
}

const summaryInfoFields = {
  default: true,
}

const saveQuoteFields = {
  ...loadQuoteFields,
  orderitems: quoteItemFields,
}

const partContentFields = {
  default: true,
  part: {
    default: true,
    packs: true,
  },
  pack: {
    default: true,
    prices: true,
  },
}

const templatedPartFields = {
  default: true,
  partImages: true,
  packs: {
    default: true,
    prices: true,
  },
  contents: true,
  category: true,
  pricingCalculator: partLoadFields.pricingCalculator,
}


export const jobCardFields = {
  default: true,
  orderItem: true,
  order: {
    default: true,
    deliveryNotes: {
      default: true,
      deliveryNoteItems: true,
    },
  },
  jobCardDepartments: true,
  jobCardOwners: {
    default: true,
    contact: true,
  },
  jobCardHistory: {
    default: true,
    contact: {fullName: true},
  },
}

// Matches the api client's own request timeout
const PENDING_SAVE_TIMEOUT = 30 * ONE_SECOND_MS
const SAVE_CONFIRMATION_TIMEOUT = 10 * ONE_SECOND_MS

export default (api) => {
  let saveOrderRequestCounter = 0
  let saveOrderInFlight = 0
  let saveOrderChain = Promise.resolve()
  const deletedItemsByRequest = new Map()

  // Queued here rather than in the saga: takeLatest cancels the saga but leaves its request on
  // the wire, and an update committing out of order re-creates items a later one deleted
  const updateQuoteInOrder = (params, fields) => {
    saveOrderInFlight++
    const sent = saveOrderChain.then(() => api.quote.update(params, fields))
    saveOrderChain = sent.then(() => saveOrderInFlight--, () => saveOrderInFlight--)
    return sent
  }

  const takeConfirmedDeletedItems = (requestCounter) => {
    if (isNil(requestCounter)) return []

    const confirmed = []
    for (const [counter, ids] of deletedItemsByRequest) {
      if (counter <= requestCounter) {
        confirmed.push(...ids)
        deletedItemsByRequest.delete(counter)
      }
    }
    return confirmed
  }

  const quoteApiWithRecent = {
    get: (params, fields, uuid, state) => {
      if (params.recent) {
        return new Promise(function (resolve) {
          resolve({
            ok: true,
            data: {
              data: [{
                count: state.quotingContext.recent.data.length,
                result: state.quotingContext.recent.data,
                success: true,
              }],
            },
          })
        })
      }
      return api.quote.get(params, fields)
    },
  }

  // Prepare the quoting context
  function* prepareContext() {
    try {
      // Make sure home contacts are loaded
      const homeContacts = yield select(state => state.data.homeContacts)
      if (homeContacts == null) yield put(dataActions.loadHomeContacts())
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadQuotingStats() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const invoiceFinancialYearStart = yield select(state => getInvoiceFinancialYearStart(state))
      const {startDate, endDate} = getDateRangeForKey(timeKey, invoiceFinancialYearStart)

      const params = {
        from: startDate,
        till: endDate,
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
      }

      const fields = {default: true}
      const response = yield call(api.quoteStats.getOne, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const stats = data.result
          yield put(quotingActions.loadQuoteStatsSuccess(stats))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadQuoteStatsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadQuoteSummaryInfo({orderId}) {
    try {
      const response = yield call(api.quote.summaryInfo, {orderId}, summaryInfoFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadQuoteSummaryInfoSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadQuoteSummaryInfoFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  const dashboardFields = {
    default: true,
    company: {
      id: true,
      name: true,
    },
  }

  function* loadUnsentQuotes() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.unsent],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
      })  // load one more, so we know if we've hit the maximum

      const response = yield call(api.quote.get, params, dashboardFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadUnsentQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadUnsentQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadUnconfirmedQuotes() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.unconfirmed],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
      })  // load one more, so we know if we've hit the maximum

      const response = yield call(api.quote.get, params, dashboardFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadUnconfirmedQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadUnconfirmedQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadNotAcceptedQuotes() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.notAccepted],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
      })  // load one more, so we know if we've hit the maximum

      const response = yield call(api.quote.get, params, dashboardFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadNotAcceptedQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadNotAcceptedQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadExpiringQuotes() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.expiring],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
      })  // load one more, so we know if we've hit the maximum

      const response = yield call(api.quote.get, params, dashboardFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadExpiringQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadExpiringQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadExpiredQuotes() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.expired],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
      })  // load one more, so we know if we've hit the maximum

      const response = yield call(api.quote.get, params, dashboardFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadExpiredQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadExpiredQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadInProductionQuotes() {
    try {

      const proSync = yield select(getProSync)
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)

      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.inProduction],
        // load one more, so we know if we've hit the maximum
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
        proSync,
      })

      const fields = {
        ...dashboardFields,
        summaryinfo: true,
      }

      const response = yield call(api.quote.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadInProductionQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadInProductionQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadRequiredSoonQuotes() {
    try {
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.requiredSoon],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        limit: dashboardPanelsMaxQuotes + 1,
      })  // load one more, so we know if we've hit the maximum

      const response = yield call(api.quote.get, params, dashboardFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadRequiredSoonQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadRequiredSoonQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadInDeliveryQuotes() {
    try {

      const proSync = yield select(getProSync)
      const {timeKey, personId, companyId} = yield select(state => state.quotingContext.dashboard)
      const {excludeHomeCompanyFromReporting} = yield select(getSettings)
      const homeCompanyId = yield select(getHomeCompanyId)
      const params = getParamsForType({
        timeKey,
        type: [QuoteStatus.inDelivery],
        personId,
        companyId: getCompanyId(companyId, homeCompanyId, excludeHomeCompanyFromReporting),
        excludeHomeCompany: excludeHomeCompanyFromReporting,
        // load one more, so we know if we've hit the maximum
        limit: dashboardPanelsMaxQuotes + 1,
        proSync,
      })

      const fields = {
        ...dashboardFields,
        summaryinfo: true,
      }

      const response = yield call(api.quote.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadInDeliveryQuoteSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadInDeliveryQuoteFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  // Requires either one of orderId or order and optionally routeParams
  function* selectQuote({orderId, order, route, contactId = null}) {
    if (orderId == null && order != null) orderId = order.id

    if (contactId === null) {
      if (route && route.match.params.contactId) {
        // Use existing route if provided
        contactId = route.match.params.contactId
      } else if (order) {
        contactId = order.contactId || get(order, 'contact.id', null)
      } else if (orderId) {
        // Must establish this information
        const response = yield call(api.quote.getOne, orderId, {contactId: true})
        yield api.handleResponse(
          response,
          function* onOk(key, data) {
            if (data && data.result) {
              contactId = data.result.contactId
            } else {
              yield put(generalError('Quote not found: ' + orderId))
            }
          },
          function* onFail(key, message) {
            yield put(generalError(message))
          }
        )
      }
    }

    // Make appropriate transition
    if (contactId) {
      const currentTab = yield select(getSelectedQuotingTab)
      yield put(push(getPathForQuote(orderId, null, contactId, currentTab)))
    }
  }

  function* searchForQuotes({searchText}) {
    try {
      const params = {
        limit: 100,
        search: searchText,
      }
      const fields = {
        default: true,
        company: {
          id: true,
          name: true,
        },
        contact: {
          id: true,
          companyId: true,
          forename: true,
          surname: true,
          mobile: true,
          phone: true,
          email: true,
          location: true,
          accountmanager: true,
        },
      }
      const response = yield call(api.quote.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.searchForQuotesSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.searchForQuotesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* reloadDashboardRequests() {
    yield put(quotingActions.loadQuoteStats())
    yield put(quotingActions.loadUnsentQuotes())
    yield put(quotingActions.loadUnconfirmedQuotes())
    yield put(quotingActions.loadNotAcceptedQuotes())
    yield put(quotingActions.loadExpiringQuotes())
    yield put(quotingActions.loadExpiredQuotes())
    yield put(quotingActions.loadInProductionQuotes())
    yield put(quotingActions.loadRequiredSoonQuotes())
    yield put(quotingActions.loadInDeliveryQuotes())
  }

  // { timeRange } is provided
  function* updateDashboardFilters({}) {
    yield call(reloadDashboardRequests)
  }

  function* attemptLoadQuote({orderId, loadRevisions}) {
    try {
      yield race({
        task: call(loadQuote, {orderId, loadRevisions}),
        cancel: take(types.QUOTING_UNLOAD_QUOTE),
      })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadQuote({orderId, loadRevisions}) {
    try {
      // Load the selected quote in full
      const response = yield call(api.quote.getOne, orderId, loadQuoteFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const quote = data.result
          yield put(quotingActions.loadQuoteSuccess(quote))
          yield put(pivotTableActions.requestData(itemPivotTableKey))
          if (loadRevisions) {
            yield put(quotingActions.loadRevisions(quote.ref))
          }
          yield put(quotingActions.autoEditMode(quote))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadQuoteFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* quoteChanged() {
    // The server is notifying us that the quote we are observing has been changed by someone else
    // or some other process
    const orderId = yield select(state => getSelectedOrderId(state))
    if (orderId) yield put(quotingActions.loadQuote(orderId, true))
  }

  function* autoEditMode({order}) {
    const user = yield select(state => state.auth.user)
    if (order.creatorId === user.contactId && canEditQuote(order, user)) {
      yield put(quotingActions.orderEditMode())
    }
  }

  function* updateViewQuotesFilters({filters}) {
    try {
      const {companyId} = filters

      // Make sure selected company is in the recent list
      if (companyId) {
        const companies = yield select(state => getOpenCompanies(state))
        if (!companies.some(r => r.id === companyId)) {
          // Load and add this company to the recents list
          const response = yield call(api.company.getOne, companyId, {default: true})
          yield api.handleResponse(
            response,
            function* onOk(key, data) {
              yield put(contactActions.addRecent(data.result))
            },
            function* onFail(key, message) {
              console.error(message)
            }
          )
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadRevisions({ref}) {
    try {
      // Load all revisions for the selected quote
      const response = yield call(api.quote.get, {
        ref,
        showInactive: true,
        showCompleted: true,
        allRevisions: true,
      }, {default: true})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadRevisionsSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadRevisionsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  // document functionality start
  function* loadQuoteDocumentsFolder({quoteId}) {
    try {
      const response = yield call(api.documents.getQuoteDocumentsFolder, quoteId)
      if (response.ok) {
        const data = response.data
        yield put(quotingActions.loadDocumentsParentFolderSuccess(data))
        const currentFolder = yield select(state => getSelectedQuoteDocumentsCurrentFolder(state))
        yield put(quotingActions.loadDocuments(currentFolder))
      } else {
        yield put(quotingActions.loadDocumentsParentFolderFailure(response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteDocuments({documentIds}) {
    try {
      yield put(snackbarMessage('Deleting your files...', 'loading', false, false))
      const response = yield call(api.documents.delete, documentIds)
      if (response.ok) {
        const currentFolder = yield select(state => getSelectedQuoteDocumentsCurrentFolder(state))
        yield put(quotingActions.loadDocuments(currentFolder))
        const failedFiles = response.data.data.failed
        if (!isEmpty(failedFiles)) {
          const failedQty = failedFiles.length
          yield put(snackbarMessage(`There was an error deleting ${failedQty} of your files`, 'error', false))
        } else {
          yield put(snackbarMessage('Your files have been deleted', 'checkmark'))
        }
      } else {
        yield put(snackbarMessage('Your files could not be deleted: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* uploadDocuments({documents}) {
    try {
      yield put(snackbarMessage('Uploading your files...', 'loading', false, false))
      // Get the current selected folder to send the data to
      const currentFolder = yield select(state => getSelectedQuoteDocumentsCurrentFolder(state))
      const response = yield call(
        api.documents.uploadDocuments,
        currentFolder.id,
        getFileFormData(documents),
        getFileFormConfig()
      )
      if (response.ok) {
        yield put(quotingActions.loadDocuments(currentFolder))
        yield put(snackbarMessage('Your files have been uploaded', 'checkmark'))
      } else {
        yield put(quotingActions.uploadDocumentsFailure(response.message, response.code))
        yield put(snackbarMessage('Your files could not be uploaded: ' + response.message, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDocuments({folder}) {
    try {
      const key = quotingContextDocumentsPivotTableKey
      yield put(pivotTableActions.loadData(key, {}))
      const response = yield call(api.documents.getDocuments, folder.id)
      if (response.ok) {
        const rootFolder = yield select(state => state.quotingContext.selected.documents.rootFolder)
        const folders = yield select(state => state.quotingContext.selected.documents.folders)

        // Check if we have opened any folders yet
        if (isEmpty(folders)) {
          yield put(quotingActions.addFolderToDocuments(folder))
        } else {
          // If the folder id isn't the same as the folder we have selected add it to the list
          // else load the root directory
          if (rootFolder.id !== folder.id) {
            // Find we are navigating to previous folder remove all the folders after
            // the one we are navigating to else add the folder to the list
            if (folders.filter(function (f) {
              return f.id === folder.id
            }).length > 0) {
              const currentIndex = 1
              const index = folders.findIndex(f => f.id === folder.id) + currentIndex
              yield put(quotingActions.removeFoldersAfterIndex(index))
            } else {
              yield put(quotingActions.addFolderToDocuments(folder))
            }
          } else {
            yield put(quotingActions.setFolderDocumentsToRootDirectory(folder))
          }
        }

        const count = get(response, 'data.data.length', 0)
        const data = get(response, 'data.data', [])
        yield put(pivotTableActions.loadDataSuccess(key, data, count))
      } else {
        yield put(pivotTableActions.loadDataFailure(key, response.message, response.code))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadQuoteInfoPopup({orderId}) {
    try {
      // Load the selected quote for the info popup
      const fields = {
        default: true,
        company: {
          default: true,
          accountingcompany: true,
        },
        contact: true,
        summaryinfo: true,
        purchaseOrderSummaryInfo: true,
      }

      const response = yield call(api.quote.getOne, orderId, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.loadQuoteInfoPopupSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.loadQuoteInfoPopupFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addApproval({approval, order, isClosing, shouldNavigateToQuote}) {
    try {
      yield put(snackbarMessage('Adding approval...'))

      const params = {approval}
      const response = yield call(api.approval.add, params, approvalDrawerFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const newApproval = data.result[0]
          const files = get(approval, 'files', [])

          if ((!isEmpty(files))) {
            for (const file of files) {
              const approvalId = get(newApproval, 'id', null)
              yield call(loadUploadUrl, {approvalId, file})
            }
          }
          if (isClosing) {
            yield put(hideApprovalDrawer())
          } else if (shouldNavigateToQuote) {
            yield put(quotingActions.selectQuote(order, null))
            yield put(hideApprovalDrawer())
          } else {
            yield put(clearApprovalDocuments())
            yield put(loadApprovalDocuments(newApproval, order))
          }
          yield put(addApprovalSuccess(newApproval))
          yield put(snackbarMessage('Approval added', 'checkmark'))
        },
        function* onFail(key, message, code) {
          yield put(addApprovalFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addApprovalRevision({approvalId}) {
    try {
      const response = yield call(api.approval.addRevision, {approvalId}, approvalDrawerFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const newApproval = data.result[0]
          yield put(addApprovalRevisionSuccess(newApproval))
          if (get(data, 'documentTransferSuccess', false)) {
            yield put(snackbarMessage('New revision created', 'checkmark'))
          } else {
            yield put(snackbarMessage('Moving files to new revision failed', 'error'))
          }
          const approvalDrawer = yield select(getSelectedQuoteApprovalDrawer)
          const order = yield select(getSelectedOrder)

          if (!isNil(newApproval) && approvalDrawer.isOpen && !isNil(order)) {
            yield put(loadApprovalDocuments(newApproval, order))
          }
        },
        function* onFails(key, message, code) {
          yield put(addApprovalRevisionFailure(message, code))
          yield put(snackbarMessage('Sorry! Something went wrong', 'error'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadUploadUrl({approvalId, file}) {
    try {
      yield put(snackbarMessage('Uploading your file...', 'loading', false, false))

      const params = {approvalId, fileName: file?.name}
      const fields = {default: true}
      const response = yield call(api.approval.getUploadUrl, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield call(uploadApprovalFile, {uploadUrl: data.result.uploadUrl, files: [file]})
        },
        function* onFail(key, message, code) {
          yield put(loadApprovalUploadUrlFailure(message, code))
          yield put(snackbarMessage(`Your file could not be uploaded: ${message}`, 'error', false))
        }
      )
    } catch (e) {
      console.error(e)
    }
  }

  function* uploadApprovalFile({uploadUrl, files}) {
    try {
      const fileSize = get(files[0], "size")
      const chunkSize = convertMBToBytes(FILE_UPLOAD_CHUNK_SIZE_MB)

      const fileAsBytes = yield call(getFileAsBytes, files)
      const fileAsChunks = getFileChunks(fileAsBytes, chunkSize, fileSize)

      for (let i = 0; i < fileAsChunks.length; i++) {
        const {fileChunk, chunkLength, range} = fileAsChunks[i]

        const headers = {
          "Content-Length": chunkLength,
          "Content-Range": range,
        }

        const response = yield call(api.oneDrive.uploadFile, uploadUrl, fileChunk, headers)
        if (!response.ok) {
          yield put(uploadFileFailure(response.message, response.code))
          yield put(snackbarMessage(`Your file could not be uploaded: ${response.message}`, 'error', false))
          return
        }
      }

      yield put(uploadFileSuccess())
      yield put(snackbarMessage('Your file has been uploaded', 'checkmark'))

    } catch (e) {
      console.error(e)
    }
  }

  function* loadApprovalsDocuments({approval, order}) {
    try {

      const {reference, revision} = approval
      const folderPath = getOrderDocumentsFolderPath(order) + getApprovalsFolderPath(reference, revision)

      const params = {
        documentProvider: 'DEFAULT',
        folderPath,
        fromRoot: false,
      }
      const fields = {default: true}
      const response = yield call(api.document.getFolder, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const sortedResults = [...data.result].sort((a, b) => a.createdDate - b.createdDate)
          yield put(loadApprovalDocumentsSuccess(sortedResults))
        },
        function* onFail(key, message, code) {
          yield put(loadApprovalDocumentsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(loadApprovalDocumentsFailure(e.message, null))
    }
  }

  function* loadApprovalRevisionDocuments({approval, order}) {
    try {
      const reference = get(approval, 'reference')
      const revision = get(approval, 'revision')
      const folderPath = getOrderDocumentsFolderPath(order) + getApprovalsFolderPath(reference, revision)

      const params = {
        documentProvider: 'DEFAULT',
        folderPath,
        fromRoot: false,
      }
      const fields = {default: true}
      const response = yield call(api.document.getFolder, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const sortedResults = [...data.result].sort((a, b) => a.createdDate - b.createdDate)
          yield put(loadApprovalRevisionDocumentsSuccess(sortedResults))
        },
        function* onFail(key, message, code) {
          yield put(loadApprovalRevisionDocumentsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(loadApprovalRevisionDocumentsFailure(e.message, null))
    }
  }

  function* loadApproval({id}) {
    try {
      const params = {baseApprovalId: id, includeAllRevisions: true}
      const response = yield call(api.approval.get, params, approvalDrawerFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const allRevisions = data.result
          const latestRevision = getLatestApprovalRevision(allRevisions)
          yield put(showApprovalDrawer(false))
          yield put(loadApprovalSuccess(latestRevision, allRevisions))

          const order = yield select(getSelectedOrder)
          if (!isNil(order)) {
            yield put(loadApprovalDocuments(latestRevision, order))
          }
        },
        function* onFail(key, message, code) {
          yield put(loadApprovalFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(loadApprovalFailure(e.message, null))
      yield put(loadApprovalDocumentsFailure(e.message, null))
      yield put(generalError(e.message))
    }
  }

  function* loadApprovalFromListView({approval, order}) {
    try {
      const baseApprovalId = get(approval, 'baseApprovalId')
      const params = {baseApprovalId, includeAllRevisions: true}
      const response = yield call(api.approval.get, params, approvalDrawerFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const allRevisions = data.result
          const latestRevision = getLatestApprovalRevision(allRevisions)
          yield put(showApprovalDrawer(false))
          yield put(loadApprovalSuccess(latestRevision, allRevisions))
          yield put(loadApprovalDocuments(latestRevision, order))
          yield put(quotingActions.loadQuote(get(order, 'id')))
          yield put(quotingActions.loadApprovalCustomerLink(get(order, 'id')))
        },
        function* onFail(key, message, code) {
          yield put(loadApprovalFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(loadApprovalFailure(e.message, null))
      yield put(loadApprovalDocumentsFailure(e.message, null))
      yield put(generalError(e.message))
    }
  }

  function* loadApprovalCustomerLink({orderId}) {
    try {
      const params = {orderId}
      const response = yield call(api.approval.customerLinkRead, params, approvalDrawerFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const approvalLink = data.result
          yield put(loadApprovalCustomerLinkSuccess(approvalLink))
        },
        function* onFail(key, message, code) {
          yield put(loadApprovalCustomerLinkFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateApproval({approval, order, isClosing, shouldNavigateToQuote}) {
    try {
      yield put(snackbarMessage('Updating approval...'))
      const params = {
        approval: {
          ...approval,
          status: get(approval, 'status'),
        },
        deletedDocumentIds: get(approval, 'deletedDocumentIds', []),
      }
      const response = yield call(api.approval.update, params, approvalDrawerFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const updatedApproval = data.result[0]

          const files = get(approval, 'files', [])
          if ((!isEmpty(files))) {
            for (const file of files) {
              const approvalId = get(updatedApproval, 'id', null)
              yield call(loadUploadUrl, {approvalId, file})
            }
          }
          const existingRevisions = yield select(state =>
            get(getSelectedQuoteApprovalRevisionsDrawer(state), 'allRevisions'))
          const updatedRevisions = getUpdatedApprovalRevisions(updatedApproval, existingRevisions)
          yield put(updateApprovalSuccess(updatedApproval, updatedRevisions))

          if (isClosing) {
            yield put(hideApprovalDrawer())
          } else if (shouldNavigateToQuote) {
            yield put(quotingActions.selectQuote(order, null))
            yield put(hideApprovalDrawer())
          } else {
            yield put(clearApprovalDocuments())
            yield put(loadApprovalDocuments(updatedApproval, order))
          }
          yield put(snackbarMessage('Approval updated', 'checkmark'))
        },
        function* onFail(key, message, code) {
          yield put(updateApprovalFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* customerUpdateApprovalStatus({token, id, status, changeText, name, isRevertingStatus}) {
    try {
      const params = {id, status, changeText, name, revert: isRevertingStatus}
      const response = yield call(api.customerViewUpdateStatus, token, params)

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(updateCustomerApprovalStatusSuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(updateCustomerApprovalStatusFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addQuote({order, copyFromId}) {
    try {
      const response = yield call(api.quote.add, {order, copyFromId})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(dialogActions.hideAddQuoteDialog())
          yield put(dialogActions.hideOpportunityDialog())
          yield put(quotingActions.addQuoteSuccess(data))
          const order = data.result
          yield put(quotingActions.selectQuote(order, null))

          const skill = yield select(state => state.onboarding.skills.RAISE_QUOTE)
          if (skill.active) yield put(onboardingActions.skillPage(SKILLS.RAISE_QUOTE.key, 3))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.addQuoteFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addOrderItem({orderItem, showMessage, closeDrawer}) {
    if (isNil(orderItem.id))
      orderItem.id = uuid()

    yield put(pivotTableActions.addEntity(itemPivotTableKey, orderItem, {recalcTotals: true}))
    if (showMessage)
      yield put(snackbarMessage(`Added ${orderItem.quantity}x ${orderItem.partCode} to quote`, 'checkmark'))

    // Reset the sub-item state
    if (!closeDrawer) {
      const {
        orderItem,
        part,
        defaults,
        template,
      } = yield select(state => state.quotingContext.selected.itemDrawer)
      yield put(setEntities(subItemPivotTableKey, []))
      yield call(itemDrawerOpened, {orderItem, part, defaults, template})
    }
  }

  function* addOrderItemAtIndex({orderItem, index}) {
    // TODO: update item indices in this and other methods...
    yield put(pivotTableActions.addEntityAtIndex(itemPivotTableKey, orderItem, index, {recalcTotals: true}))
  }

  function* editOrderItem({orderItem, index}) {
    if (orderItem.parentId) {
      // Editing a sub-item
      const data = yield select(state => state.quotingContext.selected.items.data.data)
      const parent = findItem(data, orderItem.parentId)

      if (parent && parent.subItems) {

        // Inject the changed item
        parent.subItems = parent.subItems.map(item => item.id === orderItem.id ? orderItem : item)

        // Find the applicable line item
        const allItems = getAllItems(data)
        const lineItem = findTopParent(allItems, parent.id)

        // Re-inject the line item into the grid and recalculate
        if (lineItem) {
          yield put(pivotTableActions.editEntity(itemPivotTableKey, lineItem))
          yield put(quotingActions.recalcTotals())
        }
      }
    } else {
      // Editing a line item
      yield put(pivotTableActions.editEntity(itemPivotTableKey, orderItem, index))
      yield put(quotingActions.recalcTotals())
    }
  }

  function* copyOrderItem({orderItem, recalcTotals = true}) {
    const newOrderItem = copyOrderItemHelper(orderItem)

    if (orderItem.parentId) {
      // Copying a sub-item
      const data = yield select(state => state.quotingContext.selected.items.data.data)
      const parent = findItem(data, orderItem.parentId)
      if (parent && parent.subItems) {
        parent.subItems.push(newOrderItem)
        const lineItem = findTopParent(data, parent.id)
        if (lineItem) yield put(pivotTableActions.editEntity(itemPivotTableKey, lineItem))
        if (recalcTotals) yield put(quotingActions.recalcTotals())
      }
    } else {
      // Copying a line item
      yield put(pivotTableActions.addEntity(itemPivotTableKey, newOrderItem, {recalcTotals}))
    }
  }

  function* copyOrderItems({orderItems, recalcTotals = true}) {
    if (!isNil(orderItems)) {
      for (const i in orderItems) {
        const orderItem = orderItems[i]
        yield put(quotingActions.copyOrderItem(orderItem, false))
      }
      if (recalcTotals) yield put(quotingActions.recalcTotals())
    }
  }

  const filterItem = (items, orderItem) => {
    return items.filter(item => item !== orderItem).map(item => ({
      ...item,
      subItems: item.subItems ? filterItem(item.subItems, orderItem) : null,
    }))
  }

  function* deleteOrderItem({orderItem}) {
    let items = yield select(state => state.quotingContext.selected.items.data.data)
    items = filterItem(items, orderItem).map((item, itemIndex) => {
      return {
        ...item,
        lineItem: itemIndex + 1,
        lineItemLetter: getOrderItemLetter(itemIndex + 1),
      }
    })
    yield put(quotingActions.updateOrderItems(items))
  }

  const filterItems = (items, orderItems) => {
    return difference(items, orderItems).map(item => ({
      ...item,
      subItems: item.subItems ? filterItems(item.subItems, orderItems) : null,
    }))
  }

  function* deleteOrderItems({orderItems}) {
    const items = yield select(state => state.quotingContext.selected.items.data.data)
    const remainingItems = filterItems(items, orderItems).map((item, itemIndex) => ({
      ...item,
      lineItem: itemIndex + 1,
      lineItemLetter: getOrderItemLetter(itemIndex + 1),
    }))
    yield put(quotingActions.updateOrderItems(remainingItems))
  }

  function* toggleCollapsedItems() {
    yield put(pivotTableActions.toggleCollapsedItems(itemPivotTableKey))
  }

  function* selectAllOrderItems() {
    yield put(pivotTableActions.selectAllRows(itemPivotTableKey))
  }

  function* deSelectAllOrderItems() {
    yield put(pivotTableActions.deSelectAllRows(itemPivotTableKey))
  }

  /*
   * When an order item is toggled first update the selected property in the order item in the
   * pivot table. if it's been selected we remove it from the bulk price editor
   * else we add it to the bulk price editor selected order items
   */
  function* toggleSelectedOrderItem({orderItem, index}) {
    if (!isNil(orderItem)) {
      yield put(pivotTableActions.toggleSelectedEntity(itemPivotTableKey, orderItem, index))
      if (has(orderItem, 'selected')) {
        if (orderItem['selected']) {
          yield put(quotingActions.selectOrderItem(orderItem, index))
        } else {
          yield put(quotingActions.deSelectOrderItem(orderItem, index))
        }
      } else {
        yield put(quotingActions.deSelectOrderItem(orderItem, index))
      }
    }
  }

  function* reorderOrderItem({orderItem, fromIndex, toIndex}) {
    const data = yield select(state => state.quotingContext.selected.items.data.data)

    if (orderItem.parentId) {
      // Re-ordering a sub-item
      const parent = findItem(data, orderItem.parentId)
      if (parent && parent.subItems) {
        parent.subItems = reorderItems(parent.subItems, fromIndex, toIndex).map((e, i) => ({...e, order: i}))
        const lineItem = findTopParent(data, parent.id)
        if (lineItem) yield put(pivotTableActions.editEntity(itemPivotTableKey, lineItem))
        yield put(pivotTableActions.editEntity(itemPivotTableKey, lineItem))
      }
    } else {
      // Re-ordering a line item
      const newItems = reorderItems(data, fromIndex, toIndex).map((e, i) => ({
        ...e,
        index: i,
        lineItem: i + 1,
        lineItemLetter: getOrderItemLetter(e.lineItem),
      }))
      yield put(pivotTableActions.setEntities(itemPivotTableKey, newItems))
    }

    yield put(quotingActions.recalcTotals())
  }

  function* updateSelectedOrderItems({selectedOrderItems}) {
    for (const orderItem in selectedOrderItems) {
      const newOrderItem = selectedOrderItems[orderItem]
      yield put(pivotTableActions.editEntity(itemPivotTableKey, newOrderItem, newOrderItem.index))
    }
    yield put(quotingActions.recalcTotals())
  }

  function* updateOrderItems({orderItems}) {
    yield put(pivotTableActions.updateData(itemPivotTableKey, orderItems))
    yield put(quotingActions.recalcTotals())
  }


  const mapItemsForSave = (items, deletedItems) => items.map(item => ({
    ...item,
    id: item.isNew ? -1 : item.id,
    typeId: get(item, 'type.id', 0),
    partSubTypeId: get(item, 'partSubType.id', 0),
    subItems: item.subItems
      ? mapItemsForSave(filterDeletedItems(item.subItems, deletedItems), deletedItems)
      : null,
  }))

  function* saveOrder({silent, callback, saveItems = true}) {

    silent = (isNil(silent)) ? false : silent
    try {
      let order = yield select(state => getSelectedOrder(state))
      if (order) {
        let items = saveItems
          ? yield select(state => state.quotingContext.selected.items.data.data)
          : null
        const deletedItems = saveItems
          ? yield select(state => state.quotingContext.selected.deletedItems)
          : null
        if (items) items = mapItemsForSave(filterDeletedItems(items, deletedItems), deletedItems)

        const {overrides} = order
        const userFields = parseUserFields(order)

        // Translate some values for save
        order = {
          ...order,
          userfields: userFields,
          priceMode: order.priceMode ? order.priceMode.id : 0,
          status: order.status ? order.status.value : 0,
        }

        if (silent === false) yield put(snackbarMessage("Saving your changes...", "loading", false, false))

        saveOrderRequestCounter++
        deletedItemsByRequest.set(saveOrderRequestCounter, deletedItems || [])

        let response
        try {
          response = yield call(updateQuoteInOrder, {
            order,
            items,
            deletedItems,
            overrides,
            requestCounter: saveOrderRequestCounter,
            commit: true,
          }, saveQuoteFields)
        } finally {
          yield put(quotingActions.saveOrderComplete())
        }

        yield api.handleResponse(
          response,
          function* onOk() {
            if (silent === false) yield put(snackbarMessage("Your changes have been saved", "checkmark", true))

            if (callback) {
              yield delay(300)
              callback(true)
            }
          },
          function* onFail(key, message, code) {
            yield put(quotingActions.saveOrderFailure(message, code))
            if (callback) callback(false)
          }
        )
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* waitForPendingSaves() {
    if (saveOrderInFlight === 0) return

    yield put(snackbarMessage("Saving your changes...", "loading", false, false))
    yield race({
      settled: call(function* () {
        while (saveOrderInFlight > 0) yield take(types.QUOTING_QUOTE_SAVE_COMPLETE)
      }),
      timeout: delay(PENDING_SAVE_TIMEOUT),
    })
    yield put(snackbarClear())
  }

  function* quoteSaveSuccess({data}) {
    // Inject the result to update the ids of the order items.
    const currentItems = yield select(getSelectedOrderItems)
    const pendingDeletedItems = yield select(state => state.quotingContext.selected.deletedItems)
    const updatedItems = data.result.orderitems || []
    const newItems = updateItems(currentItems, filterDeletedItemsDeep(updatedItems, pendingDeletedItems))
    yield put(quotingActions.injectSelectedQuoteItems(newItems))
  }

  function* changeOrderStatus({
                                orderId,
                                status,
                                formallyQuoted,
                                probability,
                                contactId,
                                reasonId,
                                opportunityId,
                                grossValue,
                                orderNumber,
                                expectedDate,
                                targetDate,
                                requiredDate,
                                notes,
                                addToJobBoard,
                                rejectAllRevisions,
                                tags,
                                sendAutomaticReminders,
                                callbackOnSuccess,
                                callbackOnFailure,
                              }) {
    try {
      yield put(snackbarMessage("Saving your changes...", "loading", false, false))
      const params = {
        orderId,
        status,
        formallyQuoted,
        probability,
        contactId,
        reasonId,
        opportunityId,
        grossValue,
        orderNumber,
        expectedDate,
        targetDate,
        requiredDate,
        notes,
        rejectAllRevisions,
        addToJobBoard,
        tags,
        sendAutomaticReminders,
      }

      const response = yield call(api.quote.changeStatus, params, loadQuoteFields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(snackbarClear())

          const currentOrder = yield select(state => getSelectedOrder(state))
          const userId = yield select(state => state.auth.user.id)
          const observingUsers = yield select(state => state.quotingContext.selected.observingUsers)
          //if not observing quote call success, otherwise success will be dispatched through the web socket
          if (!(currentOrder && currentOrder.id === orderId && observingUsers.hasOwnProperty(userId))) {
            yield put(quotingActions.changeOrderStatusSuccess(data))
          }

          yield put(pivotTableActions.editEntity(quotingContextQuotePivotTableKey, data.result))
          if (callbackOnSuccess) callbackOnSuccess(data.result)
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.changeOrderStatusFailure(message, code))
          if (callbackOnFailure) callbackOnFailure(message)
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* cancelEditOrder() {
    const order = yield select(state => getSelectedOrder(state))
    if (order) {
      // Reload the order
      yield put(quotingActions.loadQuote(order.id))
    }
  }

  // { orderId, order, route } are provided.
  function* previewQuote({orderId}) {
    const selectedContactId = yield select(getSelectedContactId)
    const selectedCompanyId = yield select(getSelectedCompanyId)
    yield call(waitForPendingSaves)
    yield put(push(getPathForQuotePreview(orderId, selectedCompanyId, selectedContactId)))
  }

  function* addRevision({orderId}) {
    try {
      const response = yield call(api.quoteRevision.add, {orderId})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.addRevisionSuccess(data))
          const order = data.result
          yield put(setSelectedQuotingTab('quote'))
          yield put(quotingActions.selectQuote(order, null))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.addRevisionFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* quoteSendEmail({emailDetails}) {
    try {
      yield call(waitForPendingSaves)
      const formData = getFormDataFor(emailDetails, ContactEmailType.QUOTE_ATTACHMENT)
      yield put(snackbarMessage("Sending your quotation...", "loading", false, false))
      const response = yield call(api.quote.sendEmail, formData)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.sendQuoteEmailSuccess(data))
          yield put(snackbarMessage("Your quotation has been sent", "checkmark", true))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.sendQuoteEmailFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* quoteSendApprovalEmail({emailDetails}) {
    try {
      const formData = getFormDataFor(emailDetails)
      yield put(snackbarMessage("Sending your approvals...", "loading", false, false))
      const response = yield call(api.quote.sendApprovalEmail, formData)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.sendApprovalEmailSuccess(data))
          yield put(snackbarMessage("Your approvals have been sent", "checkmark", true))

          const approvalDrawer = yield select(getSelectedQuoteApprovalDrawer)
          const approvalDetails = get(approvalDrawer, 'approvalDetails')
          const baseApprovalId = get(approvalDetails, 'baseApprovalId')

          if (!isNil(baseApprovalId) && approvalDrawer.isOpen) {
            yield put(quotingActions.loadApproval(baseApprovalId))
          }
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.sendApprovalEmailFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateOrderHistory({params}) {
    try {
      const response = yield call(api.quote.updateOrderHistory, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.updateOrderHistorySuccess(data))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.updateOrderHistoryFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* quoteSendAcceptance({emailDetails}) {
    try {
      yield call(waitForPendingSaves)
      const formData = getFormDataFor(emailDetails, ContactEmailType.QUOTE_ACCEPTANCE)
      yield put(snackbarMessage("Sending your signature request...", "loading", false, false))
      const response = yield call(api.quote.sendAcceptance, formData)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(quotingActions.sendQuoteAcceptanceSuccess(data))
          yield put(snackbarMessage("Your signature request has been sent", "checkmark", true))
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.sendQuoteAcceptanceFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* setPriceMode({priceMode}) {
    try {
      let order = yield select(state => getSelectedOrder(state))
      if (order) {
        order = {...order, priceMode: {id: priceMode}, discountPc: 0}
        yield put(quotingActions.recalcTotals(order))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* setDiscountPc({discountPc}) {
    try {
      let order = yield select(state => getSelectedOrder(state))
      if (order) {
        order = {
          ...order,
          discountPc: round(discountPc, 4),
          priceMode: {id: PriceMode.pc_discount},
          fixedPrice: null,
        }
        yield put(quotingActions.recalcTotals(order))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* setFixedPrice({fixedPrice}) {
    try {
      let order = yield select(state => getSelectedOrder(state))
      if (order) {
        order = {
          ...order,
          fixedPrice: round(fixedPrice, 2),
          priceMode: {id: PriceMode.fixed_price},
          discountPc: 0,
        }
        yield put(quotingActions.recalcTotals(order))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* setFixedMargin({fixedMargin}) {
    try {
      let order = yield select(state => getSelectedOrder(state))
      if (order) {
        order = {
          ...order,
          margin: round(fixedMargin, 2),
          priceMode: {id: PriceMode.fixed_margin},
        }
        yield put(quotingActions.recalcTotals(order))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* recalcTotals({order}) {
    try {
      if (isNil(order)) order = yield select(state => getSelectedOrder(state))
      const items = yield select(state => state.quotingContext.selected.items.data.data)
      const user = yield select(state => state.auth.user)

      order = getCalculatedTotals(order, items || [], user)
      yield put(quotingActions.updateOrder(order))
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* pivotTableItemEdited({entity, key}) {
    if (key === itemPivotTableKey) {

      // Inject sub-items into top-level items
      const allItems = yield select(state => getAllRows(state, key))
      const updatedOrderItem = getUpdatedOrderItemForSubItemUpdate(entity, allItems)
      if (updatedOrderItem) yield put(pivotTableActions.editEntity(key, updatedOrderItem))

    } else if (key === subItemPivotTableKey) {

      // Inject sub-sub items into sub-items
      const allSubItems = yield select(state => getAllRows(state, key))
      const updatedSubItem = getUpdatedParentForSubItemUpdate(entity, allSubItems)
      if (updatedSubItem) yield put(pivotTableActions.editEntity(key, updatedSubItem))
    }
  }

  function* pivotTableItemsAdded({entities, key, recalcTotals}) {
    for (const entity of entities) {
      yield call(pivotTableItemAdded, {entity, key, recalcTotals}, true)
    }
  }

  function* pivotTableItemAdded({entity, key, recalcTotals}, addOriginal = false) {
    if (key === itemPivotTableKey) {
      if (recalcTotals !== false) yield put(quotingActions.recalcTotals())

    } else if (key === subItemPivotTableKey) {
      let items = yield select(getSubItemsSelector)
      if (addOriginal) items = [...items, entity]

      if (entity.part && entity.part.type === PartType.ASSEMBLY.id) {
        yield put(pivotTableActions.setFetching(subItemPivotTableKey, true))
        const subItems = yield call(loadPartContents, {part: entity.part, parentItem: entity})

        if (assemblyHasNestedCalcWizard({subItems})) {

          // This item has a nested calc wizard - remove it and warn the user
          yield put(snackbarMessage(
            'Because this assembly contains a calc wizard, please login to your ' +
            'Clarity Pro/Enterprise system to add this to your quote',
            'error',
            false
          ))
          items = items.filter(item => item.id !== entity.id)

        } else {

          // Get the price
          const order = yield select(state => getSelectedOrder(state))
          const currencies = yield select(getCurrencies)
          const response = yield api.parts.getPrice(getPricingParams({orderItem: entity, order}))
          const pricingAccuracy = yield select(state => getPricingAccuracy(state))
          entity = yield api.handleResponse(
            response,
            function* (key, data) {
              if (data.result && data.result.length > 0) {
                const price = data.result[0]
                return getCalculatedOrderItem(entity, currencies, subItems, price, null, pricingAccuracy)
              }
              return entity
            },
            function* (key, message) {
              console.error("Failed to get part price", message)
              return entity
            }
          )

          // Inject the sub-items
          items = items.map(item => item.id === entity.id ? {...entity, subItems} : item)
        }
      }

      yield put(pivotTableActions.setEntities(key, items))
      yield put(pivotTableActions.setFetching(subItemPivotTableKey, false))
    }
  }

  const selectOrderItemSubItems = (state, orderItemId) => {
    const orderItem = getSelectedOrderItem(state, orderItemId)
    return getActiveSubItems(getAllSubItems(orderItem))
  }

  const selectActiveSubItems = (state) => {
    const subItems = getActiveSubItems(getSubItemsSelector(state))
    return explodeSubItems(subItems)
  }

  function* updateSubItemPricing({pricing, updateData, orderItemId}) {
    try {
      const currencies = yield select(getCurrencies)
      const pricingAccuracy = yield select(state => getPricingAccuracy(state))

      const orderItem = isNil(orderItemId)
        ? null
        : yield select(state => getSelectedOrderItem(state, orderItemId))

      const subItems = yield select(state => isNil(orderItemId)
        ? selectActiveSubItems(state)
        : selectOrderItemSubItems(state, orderItemId)
      )

      const pricingSubItems = get(pricing, 'subItems', [])
      const updatedSubItems = updateSubItemsWithPricing(subItems, pricingSubItems, updateData, currencies, pricingAccuracy)

      const pricingCalculatorItems = updatedSubItems.filter(isPricingCalculatorEntity)
      if (orderItem && isPricingCalculatorEntity(orderItem))
        pricingCalculatorItems.unshift(orderItem)

      const pricingCalculatorItemIds = pricingCalculatorItems.map(item => item.id)
      const pricingSubItemsArray = Object.values(pricingSubItems)

      const deletedSubItemIds = updatedSubItems
        .filter(subItem => pricingCalculatorItemIds.includes(subItem.parentId))
        .map(subItem => subItem.id)

      const filteredSubItems = updatedSubItems.filter(subItem => !deletedSubItemIds.includes(subItem.id))
      recordDeletedSubItems(pricingCalculatorItems, updatedSubItems, deletedSubItemIds)

      const subItemsWithAdditions = addSubItemsForPricingCalculators(
        filteredSubItems,
        pricingCalculatorItems,
        pricingSubItemsArray,
        yield select(getPackTypes))

      if (isNil(orderItemId)) {
        const currentSubItems = yield select(getSubItemsSelector)
        const mergedSubItems = currentSubItems.map(subItem => replaceSubItemTree(subItem, subItemsWithAdditions))
        yield put(pivotTableActions.setEntities(subItemPivotTableKey, mergedSubItems, {fromPricing: true}))

      } else {
        const updatedItem = replaceSubItemTree(orderItem, subItemsWithAdditions)
        yield put(pivotTableActions.editEntity(itemPivotTableKey, updatedItem))
        yield put(quotingActions.recalcTotals())
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* recalcTotalsFromPivotTableAction({key, recalcTotals = true}) {
    if (recalcTotals !== false && key === "quotingContext.selected.items")
      yield put(quotingActions.recalcTotals())
  }

  function* editContact({contact, callback}) {
    try {
      const response = yield call(api.contact.edit, {contact: apiHelper.contactOutbound(contact)}, {})
      // no return data as this is an optimistic action
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const contactData = data.result[0]
          yield put(quotingActions.editContactSuccess(contactData))
          if (callback) callback({success: true, data: contactData})
        },
        function* onFail(key, message, code) {
          yield put(quotingActions.editContactFailure(message, code))
          if (callback) callback({success: false, message})
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* moveOrder({order, companyId, contactId}) {
    try {
      const currentTab = yield select(getSelectedQuotingTab)
      const callback = success => {
        if (success) history.push(getPathForQuote(order.id, companyId, contactId, currentTab))
      }

      yield put(quotingActions.updateOrder({
        ...order,
        companyId,
        contactId,
      }, callback))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* quoteChangeLogWorker({callback, saveItems = true}) {
    // grab the selected quote.
    const quote = yield select(getSelectedOrder)

    // now save the change - this is a bit messy but should prevent any weird update issues
    yield call(saveOrder, {silent: true, callback, saveItems})

    // We grab the order items after update, because we want the correct ids from the server.
    const items = yield select(getSelectedOrderItems)

    // compose the change action - reducers will store the result
    yield put(quotingActions.logQuoteChange(quote, items))
  }

  function* quoteChangeUndoWorker() {
    try {
      // grab the selected quote.
      const currentOrderItems = yield select(getSelectedOrderItems) || []
      const quote = yield select(getLastOrder)
      const quoteItems = yield select(getLastOrderItems) || []

      // find any added items and mark them as deleted
      const newOrderItems =
        currentOrderItems.filter(i => quoteItems.findIndex(item => item.id === i.id) === -1)
        || []
      if (newOrderItems.length > 0) {
        yield put(quotingActions.deleteOrderItems(newOrderItems))
      }

      //put the state back
      yield put(quotingActions.injectSelectedQuoteItems(quoteItems))
      yield put(quotingActions.undoQuoteChangeData(quote))

      // now save the change
      yield put(quotingActions.saveOrder(true))
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* quoteSaveReceived({data}) {
    const user = yield select(getUser)
    const requestCounter = get(data, 'requestCounter')
    const isOwnSave = !isNil(get(data, 'user.id')) && get(data, 'user.id') === get(user, 'id')

    // Results arrive over the socket rather than on the response, so an earlier save can land last
    if (isOwnSave && !isNil(requestCounter) && requestCounter < saveOrderRequestCounter) return

    yield put(quotingActions.saveOrderSuccess(
      data,
      isOwnSave ? takeConfirmedDeletedItems(requestCounter) : []
    ))
  }

  // The save confirmation arrives over the websocket, which does not survive a
  // dropped connection. Anything waiting on it needs releasing if it never lands.
  function* saveConfirmationWatchdog() {
    const {timedOut} = yield race({
      received: take(types.QUOTING_QUOTE_SAVE_RECEIVED),
      failed: take(types.QUOTING_QUOTE_SAVE_FAILURE),
      timedOut: delay(SAVE_CONFIRMATION_TIMEOUT),
    })

    if (timedOut) {
      console.error('No save confirmation received over the websocket, releasing the item drawer')
      yield put(quotingActions.saveConfirmationTimedOut())
    }
  }

  function* quoteSaveAdvisories({data}) {
    try {
      if (data && data.length > 0) {
        yield put(snackbarMessage(data[0], 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* itemDrawerOpened({orderItem, part, defaults, template}) {
    try {
      if (template) {
        yield injectSubItemsFromTemplate({template, curSubItems: orderItem ? orderItem.subItems : null})
      } else if (showSubItems(orderItem || defaults, part)) {
        if (orderItem) {
          yield injectSubItemsForItem({orderItem})
        } else if (part) {
          yield injectSubItemsForNewItem({part})
        } else {
          yield put(quotingActions.injectSelectedQuoteSubItems([]))
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* injectSubItemsForItem({orderItem}) {
    try {
      yield put(pivotTableActions.setFetching(subItemPivotTableKey, true))
      const subItems = orderItem.subitems || (yield call(getSubItems, {orderItem}))
      const enableItem = item => ({
        ...item,
        enabled: true,
        subItems: item.subItems ? item.subItems.map(enableItem) : null,
      })

      if (orderItem.partId) {
        const response = yield call(api.partContents.get, {parentPartId: orderItem.partId}, partContentFields)

        yield api.handleResponse(
          response,
          function* onOk(key, data) {

            // Match items with part contents
            let contents = data.result
            let ret = subItems.map(item => {
              const content = contents.find(c => c.partId === item.partId)  // find the matching part content
              if (content) contents = contents.filter(c => c.id !== content.id)
              return {...enableItem(item), content}  // enable all sub-items
            })

            // Add any more content items not matching any items
            ret = [
              ...ret,
              ...contents.map(content => ({id: uuid(), enabled: false, content, part: content.part})),
            ]

            // Sort and inject in grid
            ret.sort(sortSubItems)
            yield put(quotingActions.injectSelectedQuoteSubItems(ret))
          },
          function* onFail(key, message) {
            yield put(generalError(message))
          }
        )
      } else {
        const ret = subItems.map(enableItem)  // enable all sub-items
        ret.sort(sortSubItems)
        yield put(quotingActions.injectSelectedQuoteSubItems(ret))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    } finally {
      yield put(pivotTableActions.setFetching(subItemPivotTableKey, false))
    }
  }

  function* injectSubItemsForNewItem({part}) {
    try {
      yield put(pivotTableActions.setFetching(subItemPivotTableKey, true))

      // Create items for all part contents
      const ret = yield call(loadPartContents, {part})

      // Check for calc wizards
      if (assemblyHasNestedCalcWizard({subItems: ret})) {
        yield put(snackbarMessage(
          'Because this assembly contains a calc wizard, please login to your ' +
          'Clarity Pro/Enterprise system to add this to your quote',
          'error',
          false
        ))
        yield put(quotingActions.hideItemDrawer())
      } else {
        yield put(quotingActions.injectSelectedQuoteSubItems(ret))
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    } finally {
      yield put(pivotTableActions.setFetching(subItemPivotTableKey, false))
    }
  }

  function* loadPartContents({part, parentItem}) {

    const response = yield call(api.partContents.get, {parentPartId: part.id}, partContentFields)
    return yield api.handleResponse(
      response,
      function* onOk(key, data) {
        const {currencies, packTypes} = yield select(state => state.data)
        const order = yield select(state => getSelectedOrder(state))

        // Create items for all part contents
        const ret = []
        for (let i = 0; i < data.result.length; i++) {
          const content = data.result[i]
          const item = yield call(createSubItem, {
            order,
            content,
            part: content.part,
            parentId: parentItem ? parentItem.id : null,
            currencies,
            packTypes,
          })
          // Recursively load sub-items for sub-assemblies
          if (content.part && content.part.type === PartType.ASSEMBLY.id) {
            item.subItems = yield call(loadPartContents, {part: content.part, parentItem: item})
          }

          ret.push(item)
        }

        ret.sort(sortSubItems)
        return ret
      },
      function* onFail(key, message) {
        yield put(generalError(message))
        return []
      }
    )
  }

  function* injectSubItemsFromTemplate({template, curSubItems}) {
    try {
      if (template.items) {
        yield put(pivotTableActions.setFetching(subItemPivotTableKey, true))

        const user = yield select(getUser)
        const {packTypes, currencies} = yield select(state => state.data)
        const {order} = yield select(state => state.quotingContext.selected)
        const orderTypes = yield select(state => getOrderTypes(state))
        const categories = template.items.map(item => item.partCategoryId)
        const response = yield call(api.parts.get, {containingCategory: categories}, templatedPartFields)

        yield api.handleResponse(
          response,
          function* onOk(key, data) {

            const ret = []
            const calculators = {}
            for (const i in data.result) {
              const part = data.result[i]
              const templateItem = findTemplateItem(template.items, part)

              // Find matching sub-item or create a new one
              let item = curSubItems ? curSubItems.find(i => i.partId === part.id) : null // search by part
              if (item) {
                curSubItems = curSubItems.filter(i => i.id !== item.id) // remove from the list
                item = {
                  ...item,
                  enabled: true,
                  part,
                  templateItem,
                }
              } else {
                item = {
                  id: uuid(),
                  enabled: false,
                  part,
                  templateItem,
                  ...createOrderItem({
                    user,
                    order,
                    part,
                    isSubItem: true,
                    currencies,
                    packTypes,
                    orderTypes,
                    templateItem,
                  }),
                }

                // Pricing Calculators are populated from their part, and priced without sub-items
                let subItems = null
                if (isPartTypePricingCalculator(part.type)) {
                  item.calculatorData = yield call(loadTemplateCalculatorData, part, calculators)
                } else {
                  subItems = yield call(loadPartContents, {part, parentItem: item})
                }

                // Partially-completed calculators are left out of the template
                if (isPartTypePricingCalculator(part.type) && !item.calculatorData) {
                  item = null
                } else {
                  const pricingAccuracy = yield select(state => getPricingAccuracy(state))
                  // Lookup part price now
                  const response = yield api.parts.getPrice(getPricingParams({
                    orderItem: item,
                    order,
                    subItems,
                  }))
                  item = yield api.handleResponse(
                    response,
                    function* (key, data) {
                      if (data.result && data.result.length > 0) {
                        const price = data.result[0]
                        return {
                          subItems,
                          ...getCalculatedOrderItem(item, currencies, null, price, null, pricingAccuracy),
                        }
                      }
                    },
                    function* (key, message) {
                      console.error("Failed to get part price", message)
                      return item
                    }
                  )
                }
              }
              if (item) ret.push(item)
            }

            // Add any remaining current items
            if (curSubItems) curSubItems.forEach(item => ret.push({enabled: true, ...item}))

            // Sort and inject in grid
            ret.sort(sortTemplateItems)
            // sort quick Add or PriceList Template items to the bottom of the array
            ret.sort(sortQuickAddOrPriceListTemplateItems)
            yield put(quotingActions.injectSelectedQuoteSubItems(ret))
          },
          function* onFail(key, message) {
            yield put(generalError(message))
          }
        )
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    } finally {
      yield put(pivotTableActions.setFetching(subItemPivotTableKey, false))
    }
  }

  // A template's calculator is populated from its part, or is null if it has none or it is partially-completed
  function* loadTemplateCalculatorData(part, calculators) {
    if (!part.pricingCalculator) return null

    const calculatorKey = getPricingCalculatorKey(part.pricingCalculator)
    if (!has(calculators, calculatorKey)) {
      calculators[calculatorKey] = {
        formDefinition: yield call(loadCalculatorFormDefinition, part.pricingCalculator),
        masterInputs: yield call(loadMasterCalculatorInputs, part.pricingCalculator),
      }
    }
    const {formDefinition, masterInputs} = calculators[calculatorKey]
    const calculatorData = createCalculatorDataFromPart(part, formDefinition, masterInputs)

    const formValues = buildFormDefaultInputs(formDefinition, calculatorData.inputs)
    return hasAllRequiredInputs(formValues, formDefinition, []) ? calculatorData : null
  }

  // The part's calculator does not hold its processes, so they come from the calculator's form
  function* loadCalculatorFormDefinition({name, version}) {
    yield put(fetchCalculatorForm(name, version))
    const action = yield take([types.PRICING_CALCULATOR_FORM_FETCH_SUCCESS, types.PRICING_CALCULATOR_FORM_FETCH_FAILURE])
    return get(action, 'formDefinition', null)
  }

  function* loadMasterCalculatorInputs({name, version}) {
    const response = yield call(api.pricingCalculator.getMasterInstance, name, version, partLoadFields.pricingCalculator)
    return yield api.handleResponse(
      response,
      function* onOk(key, data) {
        return get(data, 'result[0].inputs', [])
      },
      function* onFail(key, message) {
        console.error('Failed to load master pricing calculator', message)
        return []
      }
    )
  }

  const findTemplateItem = (items, part) =>
    items.find(item =>
      (item.partCategoryId === part.partCategoryId)
      || (part.categories && part.categories.indexOf(item.partCategoryId) >= 0)
    )

  const sortTemplateItems = (a, b) => {
    let ret = get(a, 'templateItem.order', 0) - get(b, 'templateItem.order', 0)
    if (ret === 0) ret = get(a, 'part.partCode', '') - get(b, 'part.partCode', '')
    return ret
  }

  const sortSubItems = (a, b) => {
    if (b.enabled && !a.enabled) return 1
    if (a.enabled && !b.enabled) return -1
    if (a.enabled && b.enabled) return a.order - b.order
    if (a.content && b.content) return a.content.order - b.content.order
    return 0
  }

  function* getSubItems({orderItem}) {
    if (orderItem.subItems) return orderItem.subItems

    // Load sub-items for order item
    const response = yield call(api.quoteItem.get, {parentId: orderItem.id}, {default: true, subitems: true})
    return yield api.handleResponse(
      response,
      function* onOk(key, data) {
        return data.result
      },
      function* onFail(key, message) {
        yield put(generalError(message))
        return []
      }
    )
  }

  function* createSubItem({order, content, part, currencies, packTypes, parentId}) {
    const orderTypes = yield select(state => getOrderTypes(state))
    const item = createOrderItem({
      order,
      part,
      content,
      isSubItem: true,
      parentId,
      currencies,
      packTypes,
      orderTypes,
    })

    const pricingAccuracy = yield select(state => getPricingAccuracy(state))
    getCalculatedOrderItem(item, currencies, null, null, null, pricingAccuracy)
    return {...item, id: uuid(), enabled: true, content}
  }

  function* quoteStatusChanged({data}) {
    try {
      // Re-load revisions list when a job is confirmed or unconfirmed
      if ([QuoteStatus.quoted, QuoteStatus.confirmed].includes(data.result.status.value)) {
        yield put(quotingActions.loadRevisions(data.result.ref))
      }
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function* loadQuoteForDelivery({orderId}) {
    try {

      const user = yield select(getUser)
      const fields = {
        default: true,
        company: true,
        orderItems: true,
        jobCards: true,
        deliveryNotes: {
          default: true,
          deliveryNoteItems: true,
        },
      }

      const response = yield call(api.quote.getOne, orderId, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const order = data.result
          yield put(loadOrderForDeliverySuccess(order))
          const search = yield select(getSearchField)

          const deliveryNoteItems = getDeliveryNoteItemsForDelivery(user, order, false, search)
          yield put(updateData(quotingContextDeliveryPivotTableKey, deliveryNoteItems))
        },
        function* onFail(key, message, code) {
          yield put(loadOrderForDeliveryFailure(message, code))
        }
      )

    } catch (e) {
      console.error(e)
      yield put(loadOrderForDeliveryFailure(e.message))
    }
  }

  function* loadDeliveryJobInformation({key}) {
    if (key === quotingContextDeliveryPivotTableKey) {
      const orderId = yield select(getSelectedOrderId)
      yield put(quotingActions.loadOrderForDelivery(orderId))
    }
  }

  function* setTaxCode({taxCodeId, updateItems}) {
    try {
      const order = yield select(getSelectedOrder)
      const taxCodes = yield select(getTaxCodes)
      const currencies = yield select(getCurrencies)
      const user = yield select(state => state.auth.user)

      const taxCode = taxCodes.find(taxCode => taxCode.id === taxCodeId)
      if (!taxCode) return

      if (updateItems) {
        // Update both order AND items, then recalculate totals
        const orderItems = yield select(getSelectedOrderItems)
        const updatedOrderItems = updateTaxCodeOnOrderItems(orderItems, taxCode, currencies)

        // Update items in the pivot table WITHOUT triggering recalcTotals
        yield put(editDataMulti(itemPivotTableKey, updatedOrderItems, false, {recalcTotals: false}))

        // Get items from state (now updated with new tax codes)
        const items = yield select(state => state.quotingContext.selected.items.data.data)

        // Calculate totals with the updated items and order
        const updatedOrder = updateTaxCodeOnOrder(order, taxCode)
        const orderWithTotals = getCalculatedTotals(updatedOrder, items || [], user)

        // Single update with everything (triggers ONE save with both order and items)
        yield put(updateOrder(orderWithTotals))
      } else {
        // Just update the order (no items to recalculate)
        yield put(updateOrder(updateTaxCodeOnOrder(order, taxCode)))
      }

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* orderCount({status, createdStart, createdEnd}) {
    try {
      const params = {
        count: true,
        createdStart,
        createdEnd,
        status,
      }

      const response = yield call(api.quote.get, params, [])
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(getOrderCountForBulkCloseSuccess(data.count, data.result))
        },
        function* onFail(key, message, code) {
          yield put(getOrderCountForBulkCloseFailure(message, code))
        }
      )
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function* closeOrRejectOrders({orderIds}) {
    try {
      const params = {
        ids: orderIds,
        count: true,
      }

      const response = yield call(api.quote.closeOrReject, params, [])
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(bulkCloseOrRejectOrdersSuccess(data.result))
          yield put(snackbarMessage(`${data.result.length} quotation(s) have been closed / rejected`))
        },
        function* onFail(key, message, code) {
          yield put(bulkCloseOrRejectOrdersFailure(message, code))
          yield put(snackbarMessage(`Unable to close / reject quotations`))
        }
      )
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.QUOTING_PREPARE_CONTEXT, prepareContext),
      takeLatest(types.QUOTING_LOAD_QUOTE_ATTEMPT, attemptLoadQuote),
      takeLatest(types.QUOTING_LOAD_SUMMARY_INFO_ATTEMPT, loadQuoteSummaryInfo),
      takeLatest(types.QUOTING_LOAD_QUOTE_STATS_ATTEMPT, loadQuotingStats),
      takeLatest(types.QUOTING_LOAD_UNSENT_QUOTES_ATTEMPT, loadUnsentQuotes),
      takeLatest(types.QUOTING_LOAD_UNCONFIRMED_QUOTES_ATTEMPT, loadUnconfirmedQuotes),
      takeLatest(types.QUOTING_LOAD_NOT_ACCEPTED_QUOTES_ATTEMPT, loadNotAcceptedQuotes),
      takeLatest(types.QUOTING_LOAD_EXPIRING_QUOTES_ATTEMPT, loadExpiringQuotes),
      takeLatest(types.QUOTING_LOAD_EXPIRED_QUOTES_ATTEMPT, loadExpiredQuotes),
      takeLatest(types.QUOTING_LOAD_IN_PRODUCTION_QUOTES_ATTEMPT, loadInProductionQuotes),
      takeLatest(types.QUOTING_LOAD_REQUIRED_SOON_QUOTES_ATTEMPT, loadRequiredSoonQuotes),
      takeLatest(types.QUOTING_LOAD_IN_DELIVERY_QUOTES_ATTEMPT, loadInDeliveryQuotes),
      takeLatest(types.QUOTING_SELECT_QUOTE, selectQuote),
      takeLatest(types.QUOTING_SEARCH_FOR_QUOTES_ATTEMPT, searchForQuotes),
      takeLatest(types.QUOTING_VIEW_DASHBOARD_UPDATE_FILTERS, updateDashboardFilters),
      takeLatest(types.QUOTING_VIEW_QUOTES_UPDATE_FILTERS, updateViewQuotesFilters),
      takeLatest(types.QUOTING_LOAD_REVISIONS_ATTEMPT, loadRevisions),
      takeLatest(types.QUOTING_QUOTE_ADD_ORDER_ITEM, addOrderItem),
      takeLatest(types.QUOTING_QUOTE_ADD_ORDER_ITEM_AT_INDEX, addOrderItemAtIndex),
      takeLatest(types.QUOTING_QUOTE_EDIT_ORDER_ITEM, editOrderItem),
      takeLatest(types.QUOTING_QUOTE_COPY_ORDER_ITEM, copyOrderItem),
      takeLatest(types.QUOTING_QUOTE_COPY_ORDER_ITEMS, copyOrderItems),
      takeLatest(types.QUOTING_QUOTE_DELETE_ORDER_ITEM, deleteOrderItem),
      takeLatest(types.QUOTING_QUOTE_DELETE_ORDER_ITEMS, deleteOrderItems),
      takeLatest(types.QUOTING_QUOTE_TOGGLE_COLLAPSED_ITEMS, toggleCollapsedItems),
      takeLatest(types.QUOTING_QUOTE_SELECT_ALL_ORDER_ITEMS, selectAllOrderItems),
      takeLatest(types.QUOTING_QUOTE_DESELECT_ALL_ORDER_ITEMS, deSelectAllOrderItems),
      takeLatest(types.QUOTING_QUOTE_UPDATE_SELECTED_ORDER_ITEMS, updateSelectedOrderItems),
      takeLatest(types.QUOTING_QUOTE_TOGGLE_SELECTED_ENTITY, toggleSelectedOrderItem),
      takeLatest(types.QUOTING_QUOTE_REORDER_ORDER_ITEM, reorderOrderItem),
      takeLatest(types.QUOTING_QUOTE_PREVIEW_QUOTE, previewQuote),
      takeLatest(types.QUOTING_QUOTE_ADD_REVISION_ATTEMPT, addRevision),
      takeLatest(types.QUOTING_QUOTE_SEND_EMAIL_ATTEMPT, quoteSendEmail),
      takeLatest(types.QUOTING_QUOTE_SEND_APPROVAL_EMAIL_ATTEMPT, quoteSendApprovalEmail),
      takeLatest(types.UPDATE_ORDER_HISTORY_ATTEMPT, updateOrderHistory),
      takeLatest(types.QUOTING_QUOTE_SEND_ACCEPTANCE_ATTEMPT, quoteSendAcceptance),
      takeLatest(types.QUOTING_QUOTE_UPDATE_ITEMS, updateOrderItems),
      takeLatest(types.QUOTING_QUOTE_AUTO_EDIT_MODE, autoEditMode),
      takeLatest(types.QUOTING_QUOTE_SET_PRICE_MODE, setPriceMode),
      takeLatest(types.QUOTING_QUOTE_SET_DISCOUNT_PC, setDiscountPc),
      takeLatest(types.QUOTING_QUOTE_SET_FIXED_PRICE, setFixedPrice),
      takeLatest(types.QUOTING_QUOTE_SET_FIXED_MARGIN, setFixedMargin),
      takeLatest(types.QUOTING_QUOTE_RECALC_TOTALS, recalcTotals),
      takeLatest(types.QUOTING_QUOTE_MOVE_ORDER, moveOrder),
      takeLatest(types.QUOTING_CONTACT_EDIT_ATTEMPT, editContact),
      takeLatest(types.QUOTING_QUOTE_SAVE_SUCCESS, quoteSaveSuccess),
      takeLatest(types.QUOTING_QUOTE_SAVE_RECEIVED, quoteSaveReceived),
      takeLatest(types.QUOTING_QUOTE_SAVE_COMPLETE, saveConfirmationWatchdog),
      takeLatest(types.QUOTING_QUOTE_SAVE_ADVISORIES, quoteSaveAdvisories),
      takeLatest(types.QUOTING_QUOTE_SHOW_ITEM_DRAWER, itemDrawerOpened),
      takeLatest(types.QUOTING_QUOTE_ADD_ATTEMPT, addQuote),
      takeLatest(types.QUOTING_QUOTE_SAVE_ATTEMPT, saveOrder),
      takeLatest(types.QUOTING_QUOTE_CHANGE_ORDER_STATUS_ATTEMPT, changeOrderStatus),
      takeLatest(types.QUOTING_QUOTE_CANCEL_EDIT, cancelEditOrder),
      takeLatest(types.QUOTING_QUOTE_CHANGED, quoteChanged),
      takeLatest(types.QUOTING_DASHBOARD_RELOAD, reloadDashboardRequests),
      takeLatest(types.QUOTING_QUOTE_CHANGE_ORDER_STATUS_SUCCESS, quoteStatusChanged),
      takeLatest(types.QUOTING_LOAD_ORDER_FOR_DELIVERY_ATTEMPT, loadQuoteForDelivery),
      takeLatest([types.PIVOT_TABLE_REQUEST_DATA, types.PIVOT_TABLE_SEARCH], loadDeliveryJobInformation),
      takeLatest(types.QUOTING_QUOTE_SET_TAX_CODE, setTaxCode),
      takeLatest(types.QUOTING_QUOTE_UPDATE_SUB_ITEM_PRICING, updateSubItemPricing),
      takeLatest(types.QUOTING_GET_ORDER_COUNT_FOR_BULK_CLOSE_ATTEMPT, orderCount),
      takeLatest(types.QUOTING_BULK_CLOSE_OR_REJECT_ORDERS_ATTEMPT, closeOrRejectOrders),

      // Document functionality
      takeLatest(types.QUOTING_DOCUMENTS_LOAD_PARENT_FOLDER_ATTEMPT, loadQuoteDocumentsFolder),
      takeLatest(types.QUOTING_DOCUMENTS_LOAD_ATTEMPT, loadDocuments),
      takeLatest(types.QUOTING_DOCUMENTS_UPLOAD_ATTEMPT, uploadDocuments),
      takeLatest(types.QUOTING_DOCUMENTS_DELETE_ATTEMPT, deleteDocuments),
      takeLatest(types.QUOTING_QUOTE_INFO_POPUP_LOAD_ATTEMPT, loadQuoteInfoPopup),

      // Approval functionality
      takeLatest(types.QUOTING_ADD_APPROVAL_ATTEMPT, addApproval),
      takeLatest(types.APPROVAL_GET_UPLOAD_URL_ATTEMPT, loadUploadUrl),
      takeLatest(types.QUOTING_LOAD_APPROVAL_ATTEMPT, loadApproval),
      takeLatest(types.QUOTING_LOAD_APPROVAL_FROM_LIST_VIEW_ATTEMPT, loadApprovalFromListView),
      takeLatest(types.QUOTING_UPDATE_APPROVAL_ATTEMPT, updateApproval),
      takeLatest(types.CUSTOMER_APPROVAL_UPDATE_STATUS_ATTEMPT, customerUpdateApprovalStatus),
      takeLatest(types.LOAD_APPROVAL_DOCUMENTS_ATTEMPT, loadApprovalsDocuments),
      takeLatest(types.LOAD_APPROVAL_REVISION_DOCUMENTS_ATTEMPT, loadApprovalRevisionDocuments),
      takeLatest(types.QUOTING_LOAD_APPROVAL_CUSTOMER_LINK_ATTEMPT, loadApprovalCustomerLink),
      takeLatest(types.QUOTING_ADD_APPROVAL_REVISION_ATTEMPT, addApprovalRevision),

      // Add autosave functionality
      takeLatest(types.QUOTING_QUOTE_UPDATE_ORDER, quoteChangeLogWorker),
      takeLatest(types.QUOTING_QUOTE_UPDATE_PROPERTY, quoteChangeLogWorker),
      takeLatest(types.QUOTING_UNDO_CHANGE, quoteChangeUndoWorker),

      // Listen to inline pivot table changes
      // IMPORTANT! It is significant that we are specifically listening to
      // addEntity and editData and NOT anything else. These two are invoked
      // PRIOR to committing changes, whereas some other calls, such as
      // updateData are invoked AFTER committing changes
      // (see actions/quoting.js:injectSelectedQuoteItems) and so responding
      // to that here causes an infinite loop.
      takeLatest(types.PIVOT_TABLE_ADD_ENTITY, pivotTableItemAdded),
      takeLatest(types.QUOTING_PRICE_LIST_ADD_ENTITIES, pivotTableItemsAdded),
      takeLatest(types.PIVOT_TABLE_EDIT_ENTITY, pivotTableItemEdited),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA, recalcTotalsFromPivotTableAction),
      takeLatest(types.PIVOT_TABLE_EDIT_DATA_MULTI, recalcTotalsFromPivotTableAction),
    ])
  }

  const viewQuotesPivotTable = createPivotTableWatcher(
    quoteApiWithRecent,
    quotingContextQuotePivotTableKey,
    [
      types.QUOTING_VIEW_QUOTES_UPDATE_FILTERS,
      types.QUOTING_QUOTE_SELECTOR_UPDATE_FILTERS,
    ],
    state => {
      const filters = getQuotingSelector(state)
      const {
        allRevisions,
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        includeFullyScheduled,
        orderTypeId,
      } = filters

      const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
      const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)

      return getQuotingFilterParams(
        allRevisions,
        getSearch(state, quotingContextQuotePivotTableKey),
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        includeFullyScheduled,
        targetStart,
        targetEnd,
        orderTypeId)
    },
    quotePivotTableFields)

  const activitiesPivotTable = createPivotTableWatcher(
    api.activity,
    activitiesTabPivotTableKey,
    [
      types.QUOTING_ACTIVITIES_UPDATE_FILTERS,
      types.QUOTING_LOAD_QUOTE_SUCCESS,
      types.DIARY_DELETE_ACTIVITY_SUCCESS,
      types.DIARY_ADD_ACTIVITY_SUCCESS,
      types.DIARY_EDIT_ACTIVITY_SUCCESS,
      types.DIARY_EDIT_ACTIVITY_UPDATE_SUCCESS,
    ],
    state => {
      const params = {
        includeCompleted: state.quotingContext.selected.activitiesTab.showCompleted,
        includeRejected: state.quotingContext.selected.activitiesTab.showRejected,
        showHiddenTypes: false,
      }
      params['orderId'] = get(state, ['quotingContext', 'selected', 'order', 'id'], 0)
      return params
    }, {
      default: true,
      activitytype: true,
      activityresource: true,
      company: {
        id: true,
        name: true,
        type: true,
      },
      contact: {
        id: true,
        companyId: true,
        forename: true,
        surname: true,
      },
      creator: {
        id: true,
        companyId: true,
        forename: true,
        surname: true,
      },
      attendees: {
        default: true,
        contact: true,
      },
      order: true,
      opportunity: true,
      owner: {
        default: true,
        contact: true,
      },
    }
  )

  const invoicesPivotTable = createPivotTableWatcher(
    api.invoice,
    invoicesTabPivotTableKey,
    [types.QUOTING_INVOICES_UPDATE_FILTERS, types.QUOTING_LOAD_QUOTE_SUCCESS],
    state => {
      return {
        orderId: get(state, ['quotingContext', 'selected', 'order', 'id'], 0),
      }
    }, {
      default: true,
      company: {
        default: true,
      },
      contact: {
        id: true,
        companyId: true,
        forename: true,
        surname: true,
      },
      createdby: {
        id: true,
        companyId: true,
        forename: true,
        surname: true,
      },
      order: {
        default: true,
        estimator: true,
      },
      taxcode: true,
      values: true,
    }
  )

  const quoteItemsPivotTable = createPivotTableWatcher(
    api.quoteItem,
    itemPivotTableKey,
    [
      types.QUOTING_ITEMS_UPDATE_FILTERS,
      types.QUOTING_LOAD_QUOTE_SUCCESS,
    ],
    state => ({
      limit: 2000,
      orderId: getSelectedOrderId(state),
    }),
    quoteItemFields
  )

  const quoteSubItemsPivotTable = createNonServerPivotTableWatcher(subItemPivotTableKey)

  const quoteAmendmentsPivotTable = createPivotTableWatcher(
    api.quoteAmendments,
    amendmentsPivotTableKey,
    [types.QUOTING_AMENDMENTS_UPDATE_FILTERS, types.QUOTING_LOAD_QUOTE_SUCCESS],
    state => ({
      orderId: getSelectedOrderId(state),
    }), {
      default: true,
      contact: {
        fullName: true,
      },
    }
  )

  const quoteSelectorOrdersPivotTable = createPivotTableWatcher(
    quoteApiWithRecent,
    quotingContextSelectorOrdersPivotTableKey,
    [
      types.QUOTING_QUOTE_SELECTOR_UPDATE_FILTERS,
    ],
    state => {
      const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
      const filters = getQuotingSelector(state)
      const {
        allRevisions,
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        includeFullyScheduled,
        orderTypeId,
        fixedStatusOptions,
      } = filters
      const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)
      const userSettings = getUserSettings(state)

      return getQuotingFilterParams(
        allRevisions,
        getSearch(state, quotingContextSelectorOrdersPivotTableKey),
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        includeFullyScheduled,
        targetStart,
        targetEnd,
        orderTypeId,
        userSettings,
        fixedStatusOptions)
    },
    quoteSelectorPivotTableFields)

  const deliveriesQuoteSelectorOrdersPivotTable = createPivotTableWatcher(
    quoteApiWithRecent,
    deliveriesQuoteSelectorPivotTableKey,
    [
      types.DELIVERIES_QUOTE_SELECTOR_UPDATE_FILTERS,
    ],
    state => {
      const invoiceFinancialYearStart = getInvoiceFinancialYearStart(state)
      const filters = getDeliveriesQuoteSelector(state)
      const {
        allRevisions,
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        includeFullyScheduled,
        orderTypeId,
        fixedStatusOptions,
        includeFullyDelivered,
      } = filters
      const {targetStart, targetEnd} = getDateFilters(filters, invoiceFinancialYearStart)
      const userSettings = getUserSettings(state)

      return getQuotingFilterParams(
        allRevisions,
        getSearch(state, deliveriesQuoteSelectorPivotTableKey),
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        includeFullyScheduled,
        targetStart,
        targetEnd,
        orderTypeId,
        userSettings,
        fixedStatusOptions,
        includeFullyDelivered)
    },
    quoteSelectorPivotTableFields)

  const quoteSelectorItemsPivotTable = createPivotTableWatcher(
    api.quoteItem,
    quotingContextSelectorOrderItemsPivotTableKey,
    [
      types.QUOTING_QUOTE_SELECTOR_LOAD_ITEMS,
    ],
    state => {
      return {
        orderId: state.quotingContext.selector.selectedOrderId,
      }
    },
    {
      default: true,
      taxcode: true,
      subitems: {
        default: true,
        calculatorData: true,
      },
      userfields: true,
      calculatorData: true,
    })

  const documentsPivotTable = createGoogleCompanyDocumentWatcher(
    api.documents,
    quotingContextDocumentsPivotTableKey,
    [],
    state => {
      const folderId = get(getSelectedQuoteDocumentsCurrentFolder(state), 'id')
      return {
        folderId,
      }
    }
  )

  const newDocumentsPivotTable = createDocumentPivotTableWatcher(
    api.document,
    quotingContextNewDocumentsPivotTableKey,
    [types.QUOTING_DOCUMENTS_SET_SELECTED_SUB_FOLDER],
    state => {
      const order = getSelectedOrder(state)
      const baseFolder = getOrderDocumentsFolderPath(order)
      const subFolder = getSelectedQuoteDocumentSubFolder(state) || ''
      return {
        documentProvider: "DEFAULT",
        folderPath: baseFolder + subFolder,
        fromRoot: false,
      }
    }
  )

  const approvalsPivotTable = createPivotTableWatcher(
    api.approval,
    quotingContextApprovalsPivotTableKey,
    [
      types.QUOTING_APPROVAL_SELECTOR_UPDATE_FILTERS,
      types.QUOTING_UPDATE_APPROVAL_SUCCESS,
    ],
    state => {
      const search = getSearch(state, quotingContextSelectorOrdersPivotTableKey)
      const filters = getApprovalsFilters(state)
      const dateFilters = getDateFilters(filters, getInvoiceFinancialYearStart(state))
      const {
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        jobTypes,
      } = filters
      const {
        targetStart,
        targetEnd,
      } = dateFilters

      return getApprovalFilterParams(
        search,
        statusTypes,
        companies,
        userTypes,
        contacts,
        dateTypes,
        targetStart,
        targetEnd,
        jobTypes
      )
    },
    {
      default: true,
      baseCreatedDate: true,
      order: {
        default: true,
        company: true,
        contact: true,
      },
    }
  )

  const quoteApprovalsPivotTable = createPivotTableWatcher(
    api.approval,
    quotingContextQuoteApprovalsPivotTableKey,
    [
      types.QUOTING_ADD_APPROVAL_SUCCESS,
      types.QUOTING_UPDATE_APPROVAL_SUCCESS,
      types.QUOTING_QUOTE_SEND_APPROVAL_EMAIL_SUCCESS,
      types.QUOTING_ADD_APPROVAL_REVISION_SUCCESS,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      return {
        orderId,
      }
    },
    {
      default: true,
      baseCreatedDate: true,
    }
  )

  const quoteHistoryPivotTable = createPivotTableWatcher(
    api.quoteHistory,
    orderHistoryPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
      types.QUOTING_QUOTE_SAVE_SUCCESS,
    ],
    state => {
      const order = getSelectedOrder(state)
      const ref = get(order, 'ref')
      return {
        ref,
      }
    },
    {
      default: true,
    }
  )

  const quoteJobCardsPivotTable = createPivotTableWatcher(
    api.jobCards,
    jobCardsPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
      types.PRODUCTION_HIDE_VIEW_JOB_CARD_DIALOG,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      return {
        orderId,
      }
    },
    jobCardFields
  )

  const quoteDeliveriesPivotTable = createPivotTableWatcher(
    api.deliveryNote,
    quotingContextDeliveryNotesPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
      types.DELIVERIES_ADD_DELIVERY_NOTE_SUCCESS,
      types.DELIVERIES_UPDATE_DELIVERY_NOTE_SUCCESS,
      types.DELIVERIES_DELETE_DELIVERY_NOTE_SUCCESS,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      return {
        orderId,
      }
    },
    {
      default: true,
      company: true,
      contact: true,
      order: {
        default: true,
        orderItems: {
          id: true,
          quantity: true,
        },
      },
      deliveryNoteItems: {
        id: true,
        quantity: true,
        orderItemId: true,
      },
      printOptions: true,
      creator: true,
      modifier: true,
    }
  )

  const quotePurchaseOrdersPivotTable = createPivotTableWatcher(
    api.purchaseOrder,
    purchasingTabPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
      types.QUOTING_PURCHASING_UPDATE_FILTERS,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      const allRevisions = getShowAllPurchaseOrderRevisions(state)
      return {
        orderId,
        allRevisions,
      }
    },
    {
      default: true,
      company: true,
      contact: true,
      creator: true,
      modifier: true,
      userfields: true,
    }
  )

  const quotePurchaseOrderItemsPivotTable = createPivotTableWatcher(
    api.purchaseOrderItem,
    quotingContextPurchasingItemsPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      return {
        orderId,
      }
    },
    {
      default: true,
      purchaseOrder: true,
    }
  )

  const quoteBillOfMaterialsPivotTable = createPivotTableWatcher(
    api.quoteItem,
    quotingContextBillOfMaterialsPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      return {
        orderId,
        allItems: true,
        includeDormant: false,
        partType: [
          PartType.MATERIAL.id,
          PartType.NON_STOCK.id,
          PartType.OUTSOURCED.id,
          PartType.SERVICE.id,
        ],
      }
    },
    {
      default: true,
      orderItem: true,
      supplier: true,
      purchaseOrderItems: true,
    }
  )

  const quoteBillOfLabourPivotTable = createPivotTableWatcher(
    api.quoteItem,
    quotingContextBillOfLabourPivotTableKey,
    [
      types.QUOTING_LOAD_QUOTE_SUCCESS,
    ],
    state => {
      const orderId = getSelectedOrderId(state)
      return {
        orderId,
        allItems: true,
        includeDormant: false,
        partType: [
          PartType.LABOUR.id,
          PartType.RESOURCE.id,
        ],
      }
    },
    {
      default: true,
      orderItem: true,
    }
  )

  function* watcher() {
    yield fork(myWatcher)
    yield fork(viewQuotesPivotTable)
    yield fork(activitiesPivotTable)
    yield fork(invoicesPivotTable)
    yield fork(documentsPivotTable)
    yield fork(newDocumentsPivotTable)
    yield fork(approvalsPivotTable)
    yield fork(quoteApprovalsPivotTable)
    yield fork(quoteHistoryPivotTable)
    yield fork(quoteSelectorOrdersPivotTable)
    yield fork(deliveriesQuoteSelectorOrdersPivotTable)
    yield fork(quoteSelectorItemsPivotTable)
    yield fork(quoteItemsPivotTable)
    yield fork(quoteSubItemsPivotTable)
    yield fork(quoteAmendmentsPivotTable)
    yield fork(quoteJobCardsPivotTable)
    yield fork(quoteDeliveriesPivotTable)
    yield fork(quotePurchaseOrdersPivotTable)
    yield fork(quotePurchaseOrderItemsPivotTable)
    yield fork(quoteBillOfMaterialsPivotTable)
    yield fork(quoteBillOfLabourPivotTable)
  }

  return {
    watcher,
  }
}
